import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Check, CheckCircle2 } from 'lucide-react';
import { AREE } from '../../constants/areas';
import { listKnownAreas } from '../../lib/areaColors';
import { fetchClosedWeek, fetchClosedWeeks } from '../../lib/api';
import { computeKrProgress } from '../../lib/krProgress';
import { useIsCoachTouched } from '../../lib/movedFlash';
import ClosedWeeksSparkline from './ClosedWeeksSparkline';
import ClosedWeeksDetailModal from './ClosedWeeksDetailModal';
import DateAssignPopover from './DateAssignPopover';

/**
 * CoachStatePanel — pannello destro shared per le 3 chat (weekly/monthly/setup).
 *
 * Render gerarchico collassabile a 2 livelli:
 *   1. Per AREA (business / lavoro / personale / salute)  — header click
 *   2. Dentro l'area: 4 sezioni (Obiettivi / KR / Azioni / MIT) — header click
 *
 * Default state: la PRIMA area è aperta con la PRIMA sezione "Obiettivi"
 * aperta. Tutto il resto è chiuso. Lo stato è persistito in localStorage
 * con chiave dipendente da `storageKey` così ogni chat ha il suo.
 *
 * Props:
 *   - title:      titolo della sezione (es. "Stato della settimana")
 *   - subtitle:   sottotitolo (es. "8 → 14 giu")
 *   - obiettivi:  array di obiettivi (filtrati al periodo)
 *   - keyResults: array di KR (con `area`, `titolo`, `valore_attuale`, `unita`)
 *   - azioni:     array di azioni (con `area`, `testo`, `fatto`, `is_bau`)
 *   - mit:        array di MIT (con `area`, `testo`, `data`, `fatto`)
 *   - storageKey: chiave localStorage per persistenza collapse (es. 'weekly')
 *   - testid:     data-testid root (es. 'week-panel')
 *   - sections:   sottoinsieme di sezioni da mostrare (default: tutte e 4).
 *                 Valori validi: 'obiettivi' | 'kr' | 'azioni' | 'mit'.
 */
const DEFAULT_SECTIONS = ['obiettivi', 'kr', 'azioni', 'mit'];

export default function CoachStatePanel({
  title, subtitle,
  obiettivi = [], keyResults = [], azioni = [], mit = [],
  storageKey = 'coach', testid = 'coach-state-panel',
  sections = DEFAULT_SECTIONS,
  highlightIds = [],
  onCloseItem = null,
  onUpdateItem = null,
}) {
  const knownAreas = listKnownAreas();
  const allAreas = knownAreas.filter((a) => AREE[a]);
  const hl = new Set(highlightIds);

  // Badge "X chiusi questa settimana" + sparkline 8 settimane.
  // Carico entrambi in parallelo, refresh quando lo stato cambia.
  const [closedWeek, setClosedWeek] = useState(null);
  const [closedTrend, setClosedTrend] = useState(null);
  const [trendModalOpen, setTrendModalOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchClosedWeek().catch(() => null),
      fetchClosedWeeks(8).catch(() => null),
    ]).then(([wk, trend]) => {
      if (cancelled) return;
      if (wk) setClosedWeek(wk);
      if (trend) setClosedTrend(trend);
    });
    return () => { cancelled = true; };
  }, [highlightIds?.length, obiettivi.length, keyResults.length, azioni.length, mit.length]);

  // Stato collapse: chiave 'area:sezione' o 'area' per top-level.
  // Default: solo `${primaArea}` aperta + `${primaArea}:<primaSezione>` aperta.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(`qp.${storageKey}.collapsed`);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Default: tutte chiuse TRANNE la prima area + prima sezione visibile
    const first = allAreas[0];
    const firstSection = sections[0] || 'obiettivi';
    const init = {};
    allAreas.forEach((a) => {
      init[a] = a !== first; // collapsed=true tranne la prima
      sections.forEach((sec) => {
        init[`${a}:${sec}`] = !(a === first && sec === firstSection);
      });
    });
    return init;
  });

  const toggle = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(`qp.${storageKey}.collapsed`, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
  };

  // Indicizzo per area (lowercase) → obiettivi / kr / azioni / mit
  const byArea = (items, getArea) => {
    const m = new Map(allAreas.map((a) => [a, []]));
    for (const it of items) {
      const a = (getArea(it) || '').toLowerCase();
      if (m.has(a)) m.get(a).push(it);
    }
    return m;
  };
  const obiByArea = byArea(obiettivi, (o) => o.area);
  // KR non ha colonna `area` direttamente nel DB (è sull'obiettivo padre):
  // arricchisco prima del raggruppamento, altrimenti il KR sparirebbe dalla
  // sidebar (era questo il bug "vedo solo Obiettivi e azioni").
  const obiAreaById = new Map(obiettivi.map((o) => [o.id, o.area]));
  const enrichedKR = keyResults.map((k) => ({
    ...k,
    area: k.area || obiAreaById.get(k.obiettivo_id),
  }));
  const krByArea = byArea(enrichedKR, (k) => k.area);
  const azByArea = byArea(azioni, (a) => a.area);
  const mitByArea = byArea(mit, (m) => {
    if (m.area) return m.area;
    // MIT può non avere area diretta: deduci da KR collegato
    const kr = m.key_result_id ? keyResults.find((k) => k.id === m.key_result_id) : null;
    return kr?.area || (kr?.obiettivo_id && obiAreaById.get(kr.obiettivo_id));
  });

  // Auto-open: quando highlightIds cambia ed è non vuoto, apri area + sezione
  // che contengono almeno un item evidenziato. Lascia il resto invariato.
  useEffect(() => {
    if (!highlightIds || highlightIds.length === 0) return;
    const hlSet = new Set(highlightIds);
    const apri = {};
    const checks = [
      ['obiettivi', obiByArea],
      ['kr', krByArea],
      ['azioni', azByArea],
      ['mit', mitByArea],
    ];
    for (const area of allAreas) {
      for (const [secKey, idx] of checks) {
        if (!sections.includes(secKey)) continue;
        const items = idx.get(area) || [];
        if (items.some((it) => hlSet.has(it.id))) {
          apri[area] = false;             // area aperta
          apri[`${area}:${secKey}`] = false; // sezione aperta
        }
      }
    }
    if (Object.keys(apri).length > 0) {
      setCollapsed((prev) => {
        const next = { ...prev, ...apri };
        try {
          localStorage.setItem(`qp.${storageKey}.collapsed`, JSON.stringify(next));
        } catch { /* ignore */ }
        return next;
      });
    }
  }, [highlightIds, storageKey]);

  return (
    <aside className="border border-line bg-paper overflow-y-auto" data-testid={testid}>
      <div className="px-5 py-4 border-b border-line sticky top-0 bg-paper z-10">
        <div className="mono-label mb-1">{title}</div>
        {subtitle && <div className="font-mono text-xs text-soft">{subtitle}</div>}
        {closedWeek && closedWeek.total > 0 && (
          <button
            type="button"
            onClick={() => setTrendModalOpen(true)}
            className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-0.5 bg-ok/10 border border-ok/30 rounded text-[10px] font-mono tabular-nums text-ok hover:bg-ok/20 hover:border-ok/50 transition-colors cursor-pointer"
            title={`MIT: ${closedWeek.breakdown?.mit ?? 0} · Azioni: ${closedWeek.breakdown?.azioni ?? 0}\nClicca per il dettaglio 26 settimane`}
            data-testid="coach-state-panel-closed-week"
            data-panel={testid}
          >
            <CheckCircle2 size={10} strokeWidth={2} />
            <span><strong>{closedWeek.total}</strong> chius{closedWeek.total === 1 ? 'o' : 'i'} questa settimana</span>
            {closedTrend?.weeks && <ClosedWeeksSparkline weeks={closedTrend.weeks} />}
          </button>
        )}
        <ClosedWeeksDetailModal open={trendModalOpen} onClose={() => setTrendModalOpen(false)} />
      </div>

      <div className="p-3">
        {allAreas.map((area) => {
          const meta = AREE[area];
          const obi = obiByArea.get(area) || [];
          const kr = krByArea.get(area) || [];
          const az = azByArea.get(area) || [];
          const mt = mitByArea.get(area) || [];
          const tot = (sections.includes('obiettivi') ? obi.length : 0)
            + (sections.includes('kr') ? kr.length : 0)
            + (sections.includes('azioni') ? az.length : 0)
            + (sections.includes('mit') ? mt.length : 0);
          const areaCollapsed = !!collapsed[area];
          return (
            <div
              key={area}
              className="mb-2 border-l-2"
              style={{ borderLeftColor: meta.color }}
              data-testid={`${testid}-area-${area}`}
            >
              <button
                type="button"
                onClick={() => toggle(area)}
                className="w-full flex items-baseline gap-2 px-3 py-2 hover:bg-linesoft/60 text-left transition-colors"
                aria-expanded={!areaCollapsed}
                data-testid={`${testid}-area-toggle-${area}`}
              >
                <span className="shrink-0" style={{ color: meta.color }}>
                  {areaCollapsed
                    ? <ChevronRight size={12} strokeWidth={1.5} />
                    : <ChevronDown size={12} strokeWidth={1.5} />}
                </span>
                <span
                  className="font-mono text-[11px] uppercase tracking-[0.2em] flex-1"
                  style={{ color: meta.color }}
                >
                  {meta.label}
                </span>
                <span className="font-mono text-[10px] text-mute tabular-nums">{tot}</span>
              </button>

              {!areaCollapsed && (
                <div className="pl-5 pb-2">
                  {sections.includes('obiettivi') && (
                    <Section
                      label="Obiettivi" items={obi} render={renderObiettivo} hl={hl}
                      entita="obiettivo" onCloseItem={onCloseItem}
                      sectionKey={`${area}:obiettivi`} collapsed={collapsed} toggle={toggle}
                      testid={`${testid}-${area}-obi`}
                    />
                  )}
                  {sections.includes('kr') && (
                    <Section
                      label="Key Result" items={kr} render={renderKr} hl={hl}
                      entita="key_result" onCloseItem={onCloseItem}
                      sectionKey={`${area}:kr`} collapsed={collapsed} toggle={toggle}
                      testid={`${testid}-${area}-kr`}
                    />
                  )}
                  {sections.includes('azioni') && (
                    <Section
                      label="Azioni" items={az} render={renderAzione} hl={hl}
                      entita="azione" onCloseItem={onCloseItem} onUpdateItem={onUpdateItem}
                      sectionKey={`${area}:azioni`} collapsed={collapsed} toggle={toggle}
                      testid={`${testid}-${area}-azioni`}
                    />
                  )}
                  {sections.includes('mit') && (
                    <Section
                      label="MIT" items={mt} render={renderMit} hl={hl}
                      entita="mit" onCloseItem={onCloseItem} onUpdateItem={onUpdateItem}
                      sectionKey={`${area}:mit`} collapsed={collapsed} toggle={toggle}
                      testid={`${testid}-${area}-mit`}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function Section({ label, items, render, sectionKey, collapsed, toggle, testid, hl, entita, onCloseItem, onUpdateItem }) {
  const isCollapsed = !!collapsed[sectionKey];
  // Render con popover date-edit attivo solo per MIT/azione e se è disponibile onUpdateItem.
  const renderWithEdit = (it) => {
    if (!onUpdateItem || !it.id) return render(it);
    if (entita === 'mit') return render(it, { onUpdate: (newData) => onUpdateItem(entita, it.id, { data: newData }, it) });
    if (entita === 'azione') return render(it, { onUpdate: (newWk) => onUpdateItem(entita, it.id, { settimana: newWk }, it) });
    return render(it);
  };
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => toggle(sectionKey)}
        className="w-full flex items-baseline gap-2 px-2 py-1 hover:bg-linesoft/40 text-left transition-colors"
        aria-expanded={!isCollapsed}
        data-testid={testid}
      >
        <span className="shrink-0 text-mute" aria-hidden>
          {isCollapsed
            ? <ChevronRight size={10} strokeWidth={1.5} />
            : <ChevronDown size={10} strokeWidth={1.5} />}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft flex-1">
          {label}
        </span>
        <span className="font-mono text-[10px] text-mute tabular-nums">{items.length}</span>
      </button>
      {!isCollapsed && (
        items.length === 0 ? (
          <p className="px-4 py-1 font-body text-xs text-mute italic">—</p>
        ) : (
          <ul className="space-y-0.5">
            {items.map((it, i) => (
              <StateRow
                key={it.id || i}
                it={it}
                entita={entita}
                isHl={!!(hl && it.id && hl.has(it.id))}
                onCloseItem={onCloseItem}
                renderWithEdit={renderWithEdit}
              />
            ))}
          </ul>
        )
      )}
    </div>
  );
}

/**
 * StateRow — singola riga della lista items in CoachStatePanel.
 * Estratta come componente per poter usare `useIsCoachTouched(id)` hook.
 *
 * Due livelli di evidenziazione (feature "vedi cosa succede in real-time"):
 *   - `isTouched` (TTL 10s) → animazione `qp-coach-touched`: pulse iniziale
 *     + fade morbido verso un giallo pastello persistente.
 *   - `isHl` (persistente finché `highlightIds` non cambia) → sfondo amber
 *     subtile classico.
 *
 * `isTouched` è più forte e temporaneo; `isHl` è più tenue e persistente.
 * Quando entrambi sono attivi, l'animazione di `isTouched` prevale visualmente
 * grazie al keyframe (che parte da amber pieno).
 */
function StateRow({ it, entita, isHl, onCloseItem, renderWithEdit }) {
  const isTouched = useIsCoachTouched(it.id);
  const isClosed = (it.stato === 'chiuso') || it.fatto === true;
  const showClose = onCloseItem && it.id && !isClosed;
  return (
    <li
      key={`${it.id || 'r'}-${isTouched ? 't' : 'x'}`}
      className={[
        'group relative',
        showClose ? 'pr-7' : '',
        isTouched ? 'qp-coach-touched' : (isHl ? 'bg-amber-50 ring-1 ring-amber-200 rounded transition-colors' : ''),
      ].filter(Boolean).join(' ')}
      data-testid={isTouched
        ? `coach-touched-${it.id}`
        : (isHl ? `coach-hl-${it.id}` : undefined)}
    >
      {renderWithEdit(it)}
      {showClose && (
        <button
          type="button"
          onClick={() => onCloseItem(entita, it.id, it)}
          className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-5 w-5 flex items-center justify-center bg-paper border border-line hover:border-accent hover:text-accent text-mute"
          title="Chiudi / segna come fatto"
          aria-label="Chiudi"
          data-testid={`close-item-${entita}-${it.id}`}
        >
          <Check size={10} strokeWidth={1.8} />
        </button>
      )}
    </li>
  );
}

function renderObiettivo(o) {
  return (
    <div className="px-3 py-1 font-body text-xs text-ink truncate" title={o.titolo}>
      {o.ordine && <span className="font-mono text-[10px] text-mute mr-1.5">{String(o.ordine).padStart(2, '0')}</span>}
      {o.titolo}
    </div>
  );
}

function renderKr(k) {
  const v = k.valore_attuale ?? k.attuale ?? null;
  const tgt = k.target_committed ?? k.target ?? null;
  const sc = formatDateIT(k.scadenza);

  // Progress 0..1 per KR numerici. Logica estratta in lib/krProgress.js
  // così è unit-testata e riutilizzabile.
  const progress = computeKrProgress(k);

  return (
    <div className="px-3 py-1 font-body text-xs">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-mute shrink-0">
          {k.natura || k.tipo || 'kr'}
        </span>
        <span className="flex-1 truncate" title={k.titolo}>{k.titolo}</span>
        {tgt != null && (
          <span className="font-mono text-[10px] tabular-nums text-soft shrink-0">
            {v ?? '—'}/{tgt}
          </span>
        )}
        {sc && (
          <span className="font-mono text-[9px] tabular-nums text-mute shrink-0" title={`Scadenza ${k.scadenza}`}>
            {sc}
          </span>
        )}
      </div>
      {progress !== null && (
        <div className="mt-1 ml-[3.4rem] flex items-center gap-1.5">
          <div className="flex-1 h-1 bg-linesoft rounded-sm overflow-hidden" data-testid={`kr-progress-${k.id}`}>
            <div
              className={`h-full ${progress >= 1 ? 'bg-ok' : progress >= 0.7 ? 'bg-ok/70' : progress >= 0.4 ? 'bg-amber-400' : 'bg-risk/70'}`}
              style={{ width: `${(progress * 100).toFixed(0)}%` }}
              title={`Avanzamento ${(progress * 100).toFixed(0)}%`}
            />
          </div>
          <span className="font-mono text-[9px] tabular-nums text-mute shrink-0 w-7 text-right">
            {(progress * 100).toFixed(0)}%
          </span>
        </div>
      )}
    </div>
  );
}

function renderAzione(a, opts = {}) {
  const wk = formatWeekIT(a.settimana);
  const chip = wk && (
    <span
      className={`font-mono text-[9px] tabular-nums shrink-0 ${opts.onUpdate ? 'text-soft hover:text-ink hover:bg-linesoft cursor-pointer px-1 -mx-1 rounded transition-colors' : 'text-mute'}`}
      title={`Settimana ${a.settimana}${opts.onUpdate ? ' — clic per spostare' : ''}`}
    >
      {wk}
    </span>
  );
  return (
    <div className="px-3 py-1 flex items-baseline gap-2 font-body text-xs">
      {a.is_bau && <span className="font-mono text-[9px] text-mute shrink-0">BAU</span>}
      <span
        className={`flex-1 truncate ${a.fatto ? 'line-through text-mute' : 'text-ink'}`}
        title={a.testo}
      >
        {a.testo}
      </span>
      {chip && opts.onUpdate ? (
        <DateAssignPopover
          kind="azione"
          currentValue={a.settimana}
          onSave={opts.onUpdate}
          side="left"
          testid={`azione-popover-${a.id}`}
        >
          <button type="button" data-testid={`azione-week-chip-${a.id}`}>{chip}</button>
        </DateAssignPopover>
      ) : chip}
    </div>
  );
}

function renderMit(m, opts = {}) {
  const d = formatDateIT(m.data);
  const chip = d && (
    <span
      className={`font-mono text-[9px] tabular-nums shrink-0 ${opts.onUpdate ? 'text-soft hover:text-ink hover:bg-linesoft cursor-pointer px-1 -mx-1 rounded transition-colors' : 'text-mute'}`}
      title={`${m.data}${opts.onUpdate ? ' — clic per spostare' : ''}`}
    >
      {d}
    </span>
  );
  return (
    <div className="px-3 py-1 flex items-baseline gap-2 font-body text-xs">
      <span
        className={`flex-1 truncate ${m.fatto ? 'line-through text-mute' : 'text-ink'}`}
        title={m.testo}
      >
        {m.testo}
      </span>
      {chip && opts.onUpdate ? (
        <DateAssignPopover
          kind="mit"
          currentValue={m.data}
          onSave={opts.onUpdate}
          side="left"
          testid={`mit-popover-${m.id}`}
        >
          <button type="button" data-testid={`mit-date-chip-${m.id}`}>{chip}</button>
        </DateAssignPopover>
      ) : chip}
    </div>
  );
}

// ── Formattatori date ─────────────────────────────────────────────────
// `formatDateIT('2026-06-30') → '30/06'`. Compatto per il pannello laterale.
function formatDateIT(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : null;
}

// `formatWeekIT('2026-W26') → 'W26'`. Solo il numero di settimana ISO.
function formatWeekIT(label) {
  if (!label || typeof label !== 'string') return null;
  const m = label.match(/W(\d{1,2})$/i);
  return m ? `W${m[1].padStart(2, '0')}` : null;
}
